import LineChart from "common/Charts/LineChart/LineChart";
import ChangeTag from "common/Tags/ChangeTag";
import React, { useMemo } from "react";
import { getPriceChangePercentage } from "features/dashboard/common/utils";
import {
  TIME_RANGE_1_MONTHS,
  TIME_RANGE_TO_SECONDS,
} from "@shared/common/constants";
import { useGetBotsGraphsData } from "@shared/features/bots/hooks";
import { useIncludeArchived } from "features/filters/hooks";
import { BotSummaryData } from "features/dashboard/common/types";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import { Clock3 } from "lucide-react";
import LookIcon from "common/Icons/LookIcon";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import Text from "common/Typography/Text";
import { getEmptyGraphData } from "common/Charts/utils";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface BotsCardChartProps {
  data: BotSummaryData;
}

const BotsCardChart = ({ data }: BotsCardChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const profitPrecision = getDisplayCurrencyAmountPrecision();
  const includeArchived = useIncludeArchived(data.botType);

  const getBotsGraphsArgs = useMemo(() => {
    return {
      bot_type: data.botType,
      time_range: TIME_RANGE_1_MONTHS,
      include_archived: includeArchived,
    };
  }, [data, includeArchived]);
  const graphData = useGetBotsGraphsData(getBotsGraphsArgs);

  const chartData = graphData.data.accumulatedProfit;
  const chartLoading = graphData.status.isFetching;
  const change =
    getPriceChangePercentage(
      chartData[0]?.y,
      chartData[chartData.length - 1]?.y
    ) || 0;

  return (
    <>
      <ChartWrapper>
        <LineChart
          data={
            !chartData.length && !chartLoading
              ? getEmptyGraphData(TIME_RANGE_TO_SECONDS[TIME_RANGE_1_MONTHS])
              : chartData
          }
          loading={chartLoading}
          displayCurrency={displayCurrency}
          precision={profitPrecision}
          isSuccess={graphData.status.isSuccess}
          hideXAxis={true}
          hideYAxis={true}
          hideTooltip={true}
        />
      </ChartWrapper>
      <ChangeWrapper>
        <SecondaryText>
          <LookIcon
            classic={<ClockIcon />}
            newLook={<NewLookClock size={12} strokeWidth={2} />}
          />
          {t("bots.one_month_change")}
        </SecondaryText>
        {!!change && (
          <ChangeTag
            value={change}
            suffix="%"
            style={{ fontSize: theme.typography.fontSizes.S }}
            arrowStyle={{ fontSize: 8 }}
          />
        )}
      </ChangeWrapper>
    </>
  );
};

export default BotsCardChart;

const ChartWrapper = styled.div`
  height: 60px;
  ${(p) => p.theme.isNewLook && "height: 44px;"}
`;
const ChangeWrapper = styled(Flex)`
  margin-top: 12px;
  ${(p) => p.theme.isNewLook && "margin-top: 8px;"}
`;
const NewLookClock = styled(Clock3)`
  margin-right: 4px;
  position: relative;
  top: 1px;
`;
const ClockIcon = styled(ClockCircleOutlined)`
  margin-right: 4px;
`;
const SecondaryText = styled(Text).attrs({ type: "secondary" })`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
